import { TwoUIProgressView, TwoUIProgressViewOperationData } from "@amx/ux-twoui-telemetry";
import { useTheme } from "@amx/ux-twoui-theming";
import { IconButton as FluentIconButton, Spinner, SpinnerSize, Stack } from "@fluentui/react";
import React, { CSSProperties, useRef } from "react";
import { useComponentLogger } from "../../shared/logging";
import { InteractiveElementProps, validateProps } from "../../shared/props";
import { ErrorCallout } from "../ErrorCallout/ErrorCallout";
import { Text } from "../Text/Text";
import { getChoiceSummaryOptionIconStyles, getChoiceSummaryOptionStyles as getInnerChoiceSummaryOptionStyles, getDisabledChoiceSummaryOptionStyles } from "./ChoiceSummary.styles";

export interface ChoiceSummaryOperationDataProps {
    /**
     * Operation data needed to present ChoiceSummary in different states.
     */
    readonly progressViewOperationData: TwoUIProgressViewOperationData;

    /**
     * Error message for when the operation fails.
     */
    readonly errorMessage: string;
}

export interface ChoiceSummaryProps extends InteractiveElementProps {
    // Options to render in the ChoiceSummary.
    readonly options: readonly ChoiceSummaryOptionProps[];

    readonly operationData?: ChoiceSummaryOperationDataProps;
}

export interface ChoiceSummaryOptionProps extends InteractiveElementProps {
    /**
     * Value displayed for the option.
     */
    readonly label: string;

    /**
     * Required callback for when the option icon is clicked.
     */
    readonly onClick: (
        event: React.MouseEvent<HTMLElement, MouseEvent>
    ) => void;

    readonly operationData?: ChoiceSummaryOperationDataProps;

    /**
     * Optional div ref to be used as the error callout target
     */
    readonly divRef?: React.RefObject<HTMLDivElement>;
}

export interface ChoiceSummaryErrorProps {
    /**
     * Error message for when the operation fails.
     */
    readonly errorMessage: string;

    /**
     * Choice summary option props
     */
    readonly optionProps: ChoiceSummaryOptionProps;
}

const choiceSummaryOptionStyles: CSSProperties = {
    listStyle: "none",
    display: "inline-flex"
};

const choiceSummarySpinnerStyles: CSSProperties = {
    ...choiceSummaryOptionStyles,
    verticalAlign: "top"
};

const ChoiceSummaryOption: React.FC<ChoiceSummaryOptionProps> = ({
    label,
    onClick,
    divRef,
    ...otherProps
}) => {
    validateProps("ChoiceSummaryOption", otherProps);

    const theme = useTheme();

    const innerStyles = otherProps.disabled ? getDisabledChoiceSummaryOptionStyles(theme) : getInnerChoiceSummaryOptionStyles(theme);
    const iconStyles = getChoiceSummaryOptionIconStyles(theme);

    const fontVariant = otherProps.disabled ? "bodyTertiaryText" : "bodyPrimaryText";
    return (
        <li style={choiceSummaryOptionStyles}>
            <Stack horizontal={true} styles={innerStyles}>
                <div ref={divRef}>
                    <FluentIconButton className="choiceSummaryIcon" styles={iconStyles} iconProps={{ iconName: "Cancel" }} onClick={!otherProps.disabled ? onClick : () => undefined} {...otherProps} />
                </div>
                <Text variant={fontVariant}>{label}</Text>
            </Stack>
        </li>
    );
};

const ChoiceSummaryBase: React.FC = ({ children, ...otherProps }) => {
    return (
        <ul aria-live="polite" {...otherProps}>
            {children}
        </ul>
    );
};

const ChoiceSummaryUpdateError: React.FC<ChoiceSummaryErrorProps> = ({
    errorMessage,
    optionProps
}) => {
    const divRef = useRef<HTMLDivElement>(null);

    return (
        <>
            <ChoiceSummaryOption divRef={divRef} {...optionProps} />
            <ErrorCallout
                targetOrTriggerButton={divRef}
                errorMessageAriaLabel={errorMessage}
                errorMessage={errorMessage}
            />
        </>
    );
};

export const ChoiceSummary: React.FC<ChoiceSummaryProps> = ({
    options,
    ...otherProps
}) => {
    useComponentLogger("ChoiceSummary", otherProps.id);

    const theme = useTheme();

    if (otherProps.operationData) {
        const { operationData } = otherProps;
        const computedSpinner = (label: string, index: number) => {
            return (
                <li style={choiceSummarySpinnerStyles} key={index}>
                    <Stack horizontal={true} styles={getInnerChoiceSummaryOptionStyles(theme)}>
                        <Spinner size={SpinnerSize.xSmall} style={{ padding: "10px", marginLeft: "4px" }} />
                        <Text>{label}</Text>
                    </Stack>
                </li>
            );
        };

        const optionsWithProgressView = options.map((option, index) => {
            const disabledByParentOrProp = otherProps.disabled || option.disabled;
            const uniqueOperationName = `${operationData.progressViewOperationData.operationName}.${option.id}`;

            return (
                <TwoUIProgressView
                    key={option.id}
                    operationData={{
                        ...operationData.progressViewOperationData,
                        operationName: uniqueOperationName
                    }}
                    updatingView={(
                        computedSpinner(option.label, index)
                    )}
                    finishedView={(
                        <ChoiceSummaryOption {...option} disabled={disabledByParentOrProp} />
                    )}
                    updateFailedView={(
                        <ChoiceSummaryUpdateError errorMessage={otherProps.operationData?.errorMessage!} optionProps={option} />
                    )}
                />
            );
        });

        return (
            <ChoiceSummaryBase>
                {optionsWithProgressView}
            </ChoiceSummaryBase>
        );
    } else {
        const computedOptions = options.map((option, index) => {
            const disabledByParentOrProp = otherProps.disabled || option.disabled;
            return (
                <ChoiceSummaryOption key={index} {...option} disabled={disabledByParentOrProp} />
            );
        });

        return (
            <ChoiceSummaryBase {...otherProps}>
                {computedOptions}
            </ChoiceSummaryBase>
        );
    }
};
